import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys, jobKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiDelete, operationsClient } from '@/lib/api/client';
import type { InvoiceListResponse } from '@/types/invoice';

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, invoiceEndpoints.delete(id)),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: invoiceKeys.lists() });
      const snapshots = new Map<string, InvoiceListResponse>();
      queryClient
        .getQueriesData<InvoiceListResponse>({ queryKey: invoiceKeys.lists() })
        .forEach(([key, data]) => {
          if (data) {
            snapshots.set(JSON.stringify(key), data);
            queryClient.setQueryData<InvoiceListResponse>(key as string[], {
              ...data,
              invoices: data.invoices.filter((inv) => inv.id !== id),
              total: data.total - 1,
            });
          }
        });
      return { snapshots };
    },
    onError: (_err, id, context) => {
      const ctx = context as { snapshots?: Map<string, InvoiceListResponse> };
      ctx?.snapshots?.forEach((data, keyStr) => {
        queryClient.setQueryData(JSON.parse(keyStr) as string[], data);
      });
      toast.error('Failed to delete invoice — reverted');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
      toast.success('Invoice deleted');
    },
  });
}
